import { v4 as uuid } from 'uuid'
import {
  createSimpleProperty,
  createTranslatedProperty,
  getTranslatedProperty
} from './properties'
import { FIELD_TYPE_DATETIME } from '../constants/fieldTypes'
import { LANGUAGES } from '../constants/languages'
import { QUESTION_SIZE_MD, QUESTION_SIZE_LG } from '../constants/layout'
import {
  QUESTION_TYPE_DESCRIPTION,
  QUESTION_TYPE_LABEL,
  QUESTION_TYPE_TITLE
} from '../constants/questionTypes'
import {
  QUESTION_PROPERTY_COLUMN,
  QUESTION_PROPERTY_DESCRIPTION,
  QUESTION_PROPERTY_LABEL,
  QUESTION_PROPERTY_PLACEHOLDER,
  QUESTION_PROPERTY_ROW,
  QUESTION_PROPERTY_SIZE,
  QUESTION_PROPERTY_TITLE,
  QUESTION_PROPERTY_TYPE,
} from '../constants/questionProperties'


// Given a question ID and form, return the page and question index
export const findQuestionLocation = (questionId, form) => {
  let questionIndex
  const pageIndex = form?.pages?.findIndex(page => {
    questionIndex = page?.questions?.findIndex(question => {
      return question.id === questionId
    })
    return questionIndex >= 0
  })
  return { pageIndex, questionIndex }
}

export const getPageQuestions = (form, pageNumber) => {
  return form?.pages?.[pageNumber]?.questions || []
}

export const getQuestionWithUpdatedSimpleValues = (question, simpleValues) => {
  return question && {
    ...question,
    simpleValues: {
      ...question.simpleValues,
      ...simpleValues
    }
  }
}

export const getQuestionGroupConfig = (questionConfigs, questionType) => {
  const config = questionConfigs?.find(q => !!q.subTypes.find(subType => subType.type === questionType))

  if (!config) {
    throw new Error(`Question of type "${questionType}" is not supported`)
  }
  return config
}

export const getQuestionConfig = (questionGroupConfig, questionType) => {
  return questionGroupConfig?.subTypes?.find(qConfig => qConfig.type === questionType)
}

export const getNewQuestion = ({
  newQuestionConfig,
  existingQuestionConfig = null,
  existingQuestion = {},
  translate,
  language = LANGUAGES.default,
  resetPropertyOnTypeSwitch = false
}) => {
  // provide default property values if not present in the existing option
  const {
    fields,
    isAdminOnly = false,
    isEnabled = true,
    isUnique = false,
    simpleValues = {},
    translatedValues = {}
  } = existingQuestion

  return {
    ...existingQuestion,
    fields: getNewFields({
      fieldConfigs: newQuestionConfig.fields,
      existingFields: fields,
      hasOtherOption: newQuestionConfig?.choice?.hasOtherOption,
      resetPropertyOnTypeSwitch,
      translate,
      language
    }),
    id: uuid(),
    isAdminOnly,
    isEnabled,
    isUnique,
    simpleValues: getNewQuestionSimpleValues(newQuestionConfig, simpleValues),
    translatedValues: getNewQuestionTranslatedValues(newQuestionConfig, existingQuestionConfig, translatedValues, translate, language, resetPropertyOnTypeSwitch),
  }
}

export const getNewFields = ({ fieldConfigs, existingFields, hasOtherOption, resetPropertyOnTypeSwitch, translate, language }) => {
  return fieldConfigs?.map((fieldConfig = {}, index) => {
    const { options: fieldConfigOptions, title, type: fieldConfigType } = fieldConfig
    const {
      defaultDisabled,
      description,
      isDate: isDateFromConfig = true,
      isTime: isTimeFromConfig = false,
      label,
      placeholder,
      values
    } = fieldConfigOptions || {}

    // provide default property values if not present in the existing option
    const {
      id = uuid(),
      isDate = isDateFromConfig,
      isTime = isTimeFromConfig,
      isUnique = false,
      options,
      simpleValues,
      translatedValues,
      type = fieldConfigType,
      validations
    } = existingFields?.[index] || {}

    const newOptions = getNewFieldOptions({
      optionConfigs: values,
      existingOptions: options,
      hasOtherOption,
      translate,
      language
    })

    const newValidations = getNewValidations({
      fieldConfig,
      existingValidations: validations,
      resetPropertyOnTypeSwitch
    })

    return {
      id,
      isEnabled: !defaultDisabled,
      isUnique,
      ...(type === FIELD_TYPE_DATETIME ? { isTime, isDate } : {}),
      simpleValues: {
        size: QUESTION_SIZE_LG,
        ...simpleValues
      },
      translatedValues: {
        ...translatedValues,
        ...createTranslatedProperty(description && QUESTION_PROPERTY_DESCRIPTION, translate(description), language),
        ...createTranslatedProperty(label && QUESTION_PROPERTY_LABEL, translate(label), language),
        ...createTranslatedProperty(placeholder && QUESTION_PROPERTY_PLACEHOLDER, translate(placeholder), language),
        ...createTranslatedProperty(QUESTION_PROPERTY_TITLE, title ? translate(title) : '', language)
      },
      type,
      ...(newOptions ? { options: newOptions } : {}),
      ...(newValidations ? { validations: newValidations } : {})
    }
  }) || null
}

export const getNewFieldOptions = ({ optionConfigs, existingOptions, hasOtherOption, translate, language }) => {
  if (existingOptions?.length > 0) {
    return hasOtherOption ? existingOptions : existingOptions.filter(option => !option.isInput)
  }

  return optionConfigs?.map((optionConfig, index) => {
    // provide default property values if not present in the existing option
    const {
      defaultChecked = false,
      id = uuid(),
      isInput = false,
      simpleValues = {},
      translatedValues = {
        title: {
          [language]: translate(optionConfig)
        }
      }
    } = existingOptions?.[index] || {}

    return {
      defaultChecked,
      id,
      isInput,
      simpleValues,
      translatedValues
    }
  }) || null
}

export const getNewQuestionSimpleValues = (questionConfig, existingSV = {}) => {
  const {
    description,
    fields,
    title,
    type,
    options,
  } = questionConfig
  const { defaultSize, placeholder = {}, choice } = options || {}

  const hasDescriptionProperty = description !== undefined
  const isDescriptionEnabled = !!existingSV.description?.enabled
  const hasPlaceholderProperty = placeholder.enabled !== undefined
  const isPlaceholderEnabled = !!existingSV.placeholder?.enabled || !!placeholder.enabled
  const hasQuestionTitleProperty = title !== undefined
  const isQuestionTitleEnabled = existingSV.title?.questionTitleEnabled === undefined ? true : existingSV.title.questionTitleEnabled
  const hasFieldTitleProperty = !!fields?.find(f => !!f.title)
  const isFieldTitleEnabled = existingSV.title?.fieldTitleEnabled === undefined ? true : existingSV.title.fieldTitleEnabled

  return {
    ...existingSV,
    ...createSimpleProperty(choice && 'choice', {
      ...existingSV.choice,
      defaultEnabled: existingSV.choice?.defaultEnabled || false,
      otherEnabled: !!choice?.hasOtherOption && !!existingSV.choice?.otherEnabled
    }),
    ...createSimpleProperty(QUESTION_PROPERTY_COLUMN, existingSV.column || 0),
    ...createSimpleProperty(hasDescriptionProperty && QUESTION_PROPERTY_DESCRIPTION, { enabled: isDescriptionEnabled }),
    ...createSimpleProperty(hasPlaceholderProperty && QUESTION_PROPERTY_PLACEHOLDER, { enabled: isPlaceholderEnabled }),
    ...createSimpleProperty(QUESTION_PROPERTY_ROW, existingSV.row || 0),
    ...createSimpleProperty(QUESTION_PROPERTY_SIZE, existingSV.size || defaultSize || QUESTION_SIZE_MD),
    ...createSimpleProperty((hasQuestionTitleProperty || hasFieldTitleProperty) && QUESTION_PROPERTY_TITLE, {
      ...(hasQuestionTitleProperty ? { questionTitleEnabled: isQuestionTitleEnabled } : {}),
      ...(hasFieldTitleProperty ? { fieldTitleEnabled: isFieldTitleEnabled } : {}),
    }),
    ...createSimpleProperty(QUESTION_PROPERTY_TYPE, type)
  }
}

export const getNewQuestionTranslatedValues = (questionConfig, existingQuestionConfig = null, existingTV = {}, translate, language, resetPropertyOnTypeSwitch) => {
  let newTitle
  let newDescription

  if (!existingQuestionConfig) { // create a brand new question
    if (questionConfig.type === QUESTION_TYPE_DESCRIPTION
      || questionConfig.type === QUESTION_TYPE_TITLE
      || questionConfig.type === QUESTION_TYPE_LABEL) {
      // use existing translatedValues for title/description/label questions
      return {
        ...existingTV,
        title: {
          [language]: ''
        }
      }
    }

    // create a title and description for regular questions
    newTitle = translate(questionConfig.title)
    newDescription = translate(questionConfig.description)
  } else { // change question type from an existing question
    const existingDescription = getTranslatedProperty( { translatedValues: existingTV }, 'description', language)
    const existingTitle = getTranslatedProperty({ translatedValues: existingTV }, 'title', language)

    // use default title value if flag is on
    if (resetPropertyOnTypeSwitch) {
      newTitle = translate(questionConfig.title)
    } else {
      const isQuestionTitleDefault = existingTitle === translate(existingQuestionConfig.title)
      newTitle = isQuestionTitleDefault ? translate(questionConfig.title) : existingTitle
    }

    // only use the default description of the new question if current description is the default for the existing question
    if (existingDescription === translate(existingQuestionConfig.description)) {
      newDescription = translate(questionConfig.description)
    } else {
      newDescription = existingDescription
    }
  }

  return {
    ...existingTV,
    title: { [language]: newTitle },
    description: { [language]: newDescription }
  }
}

export const getNewValidations = ({ fieldConfig, existingValidations, resetPropertyOnTypeSwitch }) => {
  if (existingValidations) {
    // use default validations if resetPropertyOnTypeSwitch is true
    // use existing validations if resetPropertyOnTypeSwitch is false
    return resetPropertyOnTypeSwitch ? (fieldConfig.validations?.map(validationName => ({ validationName, variables: {}})) || null) : existingValidations
  }
  return fieldConfig.validations?.map(validationName => ({ validationName, variables: {}})) || null
}

export const getLabelQuestion = (questionConfig, value, translate, language) => {
  const newQuestion = getNewQuestion({
    newQuestionConfig: questionConfig,
    translate,
    language
  })
  return {
    ...newQuestion,
    id: questionConfig.type,
    fields: [{
      ...newQuestion.fields[0],
      id: questionConfig.type,
      translatedValues: {
        title: { [language]: value || translate(questionConfig.fields[0].title) }
      }
    }]
  }
}

export const getStaticQuestion = ({ questionConfigs, type, value, translate, language = LANGUAGES.default }) => {
  const questionConfig = getQuestionConfig(getQuestionGroupConfig(questionConfigs, type), type)
  return getLabelQuestion(questionConfig, value, translate, language)
}

export const shouldShowQuestionTitle = (title, { disableTitle = false }) => {
  return (!disableTitle && typeof title !== 'undefined') || (disableTitle === true && !!title)
}

export const getFieldsWithFailedValidation = (respondentResults) => Object.keys(respondentResults || {})?.filter(key => {
  const { validation } = respondentResults[key] || {}
  const { errorMessage, isValid } = validation || {}
  return !isValid && errorMessage && key
})
